import { ZodSchemaInfo } from '~/prix/types/zodType'
import { GenericObject } from '~/prix/types/type'
import formatBasedOnType from '~/prix/utils/formatBasedOnType'
import { ClusterListLayer } from './clusterListInput.util'

export type ClusterListColumn = {
  key: string
  label: string
  shape?: ZodSchemaInfo
}

const maskDocument = (value: string, key: string) => {
  if (key.toLowerCase().endsWith('cnpj') && /^\d{14}$/.test(value)) {
    return value.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  }

  if (key.toLowerCase().endsWith('cpf') && /^\d{11}$/.test(value)) {
    return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }

  return value
}

const shapeRecordFromLayer = (layer: ClusterListLayer) =>
  ((layer.variable.shape as unknown as ZodSchemaInfo | undefined)?.shape ?? {}) as Record<
    string,
    ZodSchemaInfo
  >

const humanizeFieldKey = (key: string) =>
  key
    .split('.')
    .at(-1)
    ?.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim() ?? key

export function getClusterListColumns(layer: ClusterListLayer): ClusterListColumn[] {
  const shapeRecord = shapeRecordFromLayer(layer)
  const basicSelect = Array.from(new Set(layer.variable.basicSelect ?? []))
  const validBasicSelect = basicSelect.filter(key => shapeRecord[key])

  const fallbackKeys =
    validBasicSelect.length > 0
      ? validBasicSelect
      : Object.keys(shapeRecord).filter(key =>
          ['name', 'title', 'socialName', 'tradeName', 'corporateName', 'fullName'].includes(key),
        )

  const selectedKeys = (fallbackKeys.length > 0 ? fallbackKeys : Object.keys(shapeRecord)).slice(0, 5)

  return selectedKeys.map(key => ({
    key,
    label: shapeRecord[key]?.label || humanizeFieldKey(key),
    shape: shapeRecord[key],
  }))
}

export function formatClusterListValue(record: GenericObject, column: ClusterListColumn) {
  const rawValue = record[column.key]
  if (rawValue === null || typeof rawValue === 'undefined' || rawValue === '') {
    return '-'
  }

  if (typeof rawValue === 'string') {
    const maskedValue = maskDocument(rawValue, column.key)
    if (maskedValue !== rawValue) {
      return maskedValue
    }
  }

  if (column.shape) {
    return formatBasedOnType(rawValue, column.shape, '-') ?? '-'
  }

  return String(rawValue)
}

export function buildClusterListItems(items: GenericObject[], columns: ClusterListColumn[]) {
  return items.map(item => ({
    ...item,
    searchableText: Array.from(
      new Set(
        [
          ...columns.map(column => formatClusterListValue(item, column)),
          ...Object.values(item)
            .filter(value => ['string', 'number', 'boolean'].includes(typeof value))
            .map(value => String(value)),
        ]
          .filter(Boolean)
          .map(value => value.toLowerCase()),
      ),
    ).join(' '),
  }))
}

export function getClusterListGridTemplate(columns: ClusterListColumn[]) {
  if (columns.length <= 1) {
    return 'minmax(0, 1fr) 28px'
  }

  return [ 'minmax(0, 1.6fr)', ...Array.from({ length: columns.length - 1 }, () => 'minmax(0, 1fr)'), '28px' ].join(' ')
}
